import React, { useState, useEffect, useCallback } from 'react';
import { X, Globe, Settings, Zap, AlertCircle, CheckCircle, Clock, RefreshCw, StopCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const SitemapConfigModal = ({ client, isOpen, onClose, onSave }) => {
  const [config, setConfig] = useState({
    sitemapUrl: '',
    autoSync: false,
    syncFrequency: 'weekly',
    maxPages: 50,
    includePaths: [],
    excludePaths: []
  });
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState(null);
  const [newIncludePath, setNewIncludePath] = useState('');
  const [newExcludePath, setNewExcludePath] = useState('');

  const fetchSitemapConfig = useCallback(async () => {
    try {
      const response = await axios.get(`/sitemap/config/${client._id}`);
      setConfig({
        sitemapUrl: response.data.sitemapConfig.sitemapUrl || '',
        autoSync: response.data.sitemapConfig.autoSync || false,
        syncFrequency: response.data.sitemapConfig.syncFrequency || 'weekly',
        maxPages: response.data.sitemapConfig.maxPages || 50,
        includePaths: response.data.sitemapConfig.includePaths || [],
        excludePaths: response.data.sitemapConfig.excludePaths || []
      });
    } catch (error) {
      console.error('Failed to fetch sitemap config:', error);
    }
  }, [client._id]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`/sitemap/status/${client._id}`);
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  }, [client._id]);

  useEffect(() => {
    if (isOpen && client) {
      fetchSitemapConfig();
      fetchStatus();
    }
  }, [isOpen, client, fetchSitemapConfig, fetchStatus]);

  const handleSave = async () => {
    try {
      setProcessing(true);
      await axios.put(`/sitemap/config/${client._id}`, config);
      toast.success('Sitemap configuration saved successfully');
      onSave && onSave();
    } catch (error) {
      toast.error('Failed to save sitemap configuration');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const startProcessing = async () => {
    try {
      setProcessing(true);
      await axios.post(`/sitemap/process/${client._id}`);
      toast.success('Sitemap processing started');
      
      // Poll for status updates
      const pollStatus = setInterval(async () => {
        try {
          const response = await axios.get(`/sitemap/status/${client._id}`);
          const currentStatus = response.data;
          setStatus(currentStatus);
          
          if (currentStatus?.processingStatus !== 'syncing') {
            clearInterval(pollStatus);
            setProcessing(false);
            toast.success('Sitemap processing completed!');
          }
        } catch (error) {
          clearInterval(pollStatus);
          setProcessing(false);
          console.error('Polling error:', error);
        }
      }, 3000);
      
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start processing');
      setProcessing(false);
    }
  };

  const cancelProcessing = async () => {
    try {
      setProcessing(true);
      await axios.post(`/sitemap/cancel/${client._id}`);
      toast.success('Sitemap processing cancelled');
      await fetchStatus();
      setProcessing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel processing');
      setProcessing(false);
    }
  };

  const addIncludePath = () => {
    if (newIncludePath && !config.includePaths.includes(newIncludePath)) {
      setConfig(prev => ({
        ...prev,
        includePaths: [...prev.includePaths, newIncludePath]
      }));
      setNewIncludePath('');
    }
  };

  const addExcludePath = () => {
    if (newExcludePath && !config.excludePaths.includes(newExcludePath)) {
      setConfig(prev => ({
        ...prev,
        excludePaths: [...prev.excludePaths, newExcludePath]
      }));
      setNewExcludePath('');
    }
  };

  const removeIncludePath = (index) => {
    setConfig(prev => ({
      ...prev,
      includePaths: prev.includePaths.filter((_, i) => i !== index)
    }));
  };

  const removeExcludePath = (index) => {
    setConfig(prev => ({
      ...prev,
      excludePaths: prev.excludePaths.filter((_, i) => i !== index)
    }));
  };

  const getStatusIcon = () => {
    switch (status?.processingStatus) {
      case 'syncing': return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    switch (status?.processingStatus) {
      case 'syncing': return 'Processing sitemap...';
      case 'completed': return 'Last sync completed successfully';
      case 'failed': return 'Last sync failed';
      default: return 'Not processed yet';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Globe className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI-Powered Sitemap Import</h2>
              <p className="text-sm text-gray-600">Automatically generate contexts from website content</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Status Section */}
          {status && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  {getStatusIcon()}
                  <span className="ml-2 font-medium">{getStatusText()}</span>
                </div>
                {status.lastSync && (
                  <span className="text-sm text-gray-500">
                    Last sync: {new Date(status.lastSync).toLocaleString()}
                  </span>
                )}
              </div>
              
              {status.stats && (
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{status.currentStats?.totalPages || 0}</div>
                    <div className="text-xs text-gray-600">Total Pages</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{status.currentStats?.processedPages || 0}</div>
                    <div className="text-xs text-gray-600">Processed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">{status.currentStats?.failedPages || 0}</div>
                    <div className="text-xs text-gray-600">Failed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">{status.stats?.newContexts || 0}</div>
                    <div className="text-xs text-gray-600">New Contexts</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Configuration */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Basic Configuration
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sitemap URL *
                </label>
                <input
                  type="url"
                  value={config.sitemapUrl}
                  onChange={(e) => setConfig(prev => ({ ...prev, sitemapUrl: e.target.value }))}
                  placeholder="https://yourwebsite.com/sitemap.xml"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usually found at /sitemap.xml or /sitemap_index.xml
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sync Frequency
                  </label>
                  <select
                    value={config.syncFrequency}
                    onChange={(e) => setConfig(prev => ({ ...prev, syncFrequency: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Pages
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="200"
                    value={config.maxPages}
                    onChange={(e) => setConfig(prev => ({ ...prev, maxPages: parseInt(e.target.value) }))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoSync"
                  checked={config.autoSync}
                  onChange={(e) => setConfig(prev => ({ ...prev, autoSync: e.target.checked }))}
                  className="mr-3"
                />
                <label htmlFor="autoSync" className="text-sm font-medium text-gray-700">
                  Enable automatic syncing
                </label>
              </div>
            </div>

            {/* Advanced Filtering */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Advanced Filtering</h3>
              
              {/* Include Paths */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Include Only Paths (optional)
                </label>
                <div className="flex mb-2">
                  <input
                    type="text"
                    value={newIncludePath}
                    onChange={(e) => setNewIncludePath(e.target.value)}
                    placeholder="/products/, /services/"
                    className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && addIncludePath()}
                  />
                  <button
                    type="button"
                    onClick={addIncludePath}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {config.includePaths.map((path, index) => (
                    <span key={index} className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                      {path}
                      <button
                        onClick={() => removeIncludePath(index)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Exclude Paths */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exclude Paths (optional)
                </label>
                <div className="flex mb-2">
                  <input
                    type="text"
                    value={newExcludePath}
                    onChange={(e) => setNewExcludePath(e.target.value)}
                    placeholder="/blog/, /admin/"
                    className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && addExcludePath()}
                  />
                  <button
                    type="button"
                    onClick={addExcludePath}
                    className="px-4 py-2 bg-red-600 text-white rounded-r-md hover:bg-red-700"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {config.excludePaths.map((path, index) => (
                    <span key={index} className="inline-flex items-center bg-red-100 text-red-800 px-2 py-1 rounded text-sm">
                      {path}
                      <button
                        onClick={() => removeExcludePath(index)}
                        className="ml-1 text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* AI Processing Info */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Zap className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">AI-Powered Processing:</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Content is automatically summarized and categorized</li>
                  <li>Relevant keywords and tags are generated</li>
                  <li>Business relevance is scored (0-100%)</li>
                  <li>Only high-relevance pages (60%+) become contexts</li>
                  <li>Lithuanian language optimization included</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {status?.processingStatus === 'syncing' 
              ? 'Processing sitemap - this may take several minutes...' 
              : config.sitemapUrl 
                ? 'Ready to process sitemap' 
                : 'Enter sitemap URL to get started'
            }
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={processing}
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={!config.sitemapUrl || processing}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
            >
              Save Config
            </button>
            
            {status?.processingStatus === 'syncing' ? (
              <button
                onClick={cancelProcessing}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                <StopCircle className="w-4 h-4 mr-2" />
                Cancel Processing
              </button>
            ) : (
              <button
                onClick={startProcessing}
                disabled={!config.sitemapUrl || processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {processing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Start AI Processing
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitemapConfigModal;